import { Injectable } from "@angular/core";
import { Observable, Observer, Subject, forkJoin } from 'rxjs';
import { map, take, mergeMap } from 'rxjs/operators';

import { ContentParser } from './content.parser';
import { ContentConfig, LangChangeEvent } from './content.config';

import { ContentService } from './content.service';
import { isNullOrEmpty } from '@awp/core/utils';

export interface ContentChangeEvent {
  translations: any;
  lang: string;
}

@Injectable({
  providedIn: 'root'
})
export class ContentProvider {

  public onLangChanged: Subject<LangChangeEvent>;

  /**
   * @param config An instance of the configuration specifying the current lang
   * @param store An instance of the store for all retrieved keys
   * @param parser An instance of the parser currently used
   */
  constructor(
    private config: ContentConfig,
    private contentService: ContentService,
    private parser: ContentParser) {
    this.onLangChanged = config.onCurrentLangChange;
  }

  /**
   * Gets the translated value of a key (or an array of keys)
   * @returns the translated key, or an object of translated keys
   */
  public get<T>(key: string, interpolateParams?: Object, rootContent?: any): Observable<T> {
    if (key == null || !key.length) {
      throw new Error(`Parameter "key" required`);
    }

    return Observable.create((observer: Observer<string>) => {
      const onComplete = (res: string) => {
        observer.next(res);
        observer.complete();
      };
      const onError = (err: any) => {
        observer.error(err);
      };

      const parseContentResult = (target: any, subKey: any, interpolateParams: Object) => {
        const result = this.getParsedResult(target, subKey, interpolateParams);

        if (result != null && typeof result.subscribe === "function") {
          result.pipe(take(1))
            .subscribe(data => {
              parseContentResult(data.target, data.key, interpolateParams);
            },
            onError);
        } else {
          onComplete(result);
        }
      }

      if (rootContent == null) {
        this.fetchRoot()
          .pipe(take(1))
          .subscribe(content => {
            parseContentResult(content, key, interpolateParams);
          },
          onError);
      } else {
        parseContentResult(rootContent, key, interpolateParams);
      }
    });
  }

  public getRawValue<T>(key: string): Observable<T> {
    if (key == null || !key.length) {
      throw new Error(`Parameter "key" required`);
    }

    return Observable.create((observer: Observer<string>) => {
      const onComplete = (res: string) => {
        observer.next(res);
        observer.complete();
      };
      const onError = (err: any) => {
        observer.error(err);
      };

      const getRawValue = (target: any, subKey: any) => {
        const result = this._getKeyValue(target, subKey);

        if (result != null && typeof result.subscribe === "function") {
          result.pipe(take(1))
            .subscribe(data => {
              if (typeof data === 'string') {
                onComplete(data);
              } else {
                getRawValue(data.target, data.key);
              }
            },
            onError);
        } else {
          onComplete(result);
        }
      }

      this.fetchRoot()
        .pipe(take(1))
        .subscribe(content => {
          getRawValue(content, key);
        },
        onError);
    });
  }

  /**
   * Returns the raw value result of the content
   */
  private _getKeyValue(content: any, key: any): any {
    if (typeof content === 'string' || isNullOrEmpty(key)) {
      return content;
    }

    let result: string | Observable<string>;

    const value = this.getValue(content, key);

    if (value.link != null) {
      return this.fetchContent(value.link.href, value.link.type)
        .pipe(
          map(p => {
            return {
              key: value.keys,
              target: p
            }
        }));
    }

    if (value.target != null) {
      if (typeof value.target === 'string') {
        result = value.target;
        return result != null ? result : key;
      } else if (value.target.href != null && value.target.type === 'html') {
        return this.fetchContent(value.target.href, value.target.type);
      } else {
        return value.target;
      }
    }

    return undefined;
  }


  /**
   * Returns the parsed result of the content
   */
  public getParsedResult(content: any, key: any, interpolateParams?: Object): any {
    if (typeof content === 'string') {
      return this.parserInterpolate(content, interpolateParams);
    }
    if (isNullOrEmpty(key)) {
      return content;
    }

    let result: string | Observable<string>;

    const value = this.getValue(content, key);

    if (value.link != null) {
      return this.fetchContent(value.link.href, value.link.type)
        .pipe(
          map(p => {
            return {
              key: value.keys,
              target: p
            }
        }));
    }

    if (value.target != null) {
      if (typeof value.target === 'string') {
        result = this.parserInterpolate(value.target, interpolateParams);
        return result != null ? result : key;
      } else {
        return value.target;
      }
    }

    return undefined;
  }

  public parserInterpolate(content: any, params: Object): any {
    const result = this.parser.interpolate(content, params);
    return result;
  }

  public fetchRoot(): Observable<any> {
    return this.fetchContent('root');
  }

  public fetchContent<T>(url: 'root' | string, type = 'json'): Observable<T> {
    return this.contentService.getContent(url, this.config.currentLang, type).pipe(take(1));
  }

  public preFetchContent(keys: string[]): Observable<any> {
    return this.fetchRoot()
      .pipe(
        mergeMap(content => {
          const urls = keys.map(key => {
            const value = this.getValue(content, key);
            return value != null && value.link != null ? value.link.href : null
          }).filter(Boolean);

          const o = urls.map(url => this.fetchContent(url));
          return forkJoin(o);
        }),
        take(1)
      );
  }

  private getValue(target: any, key: string): any {
    let keys = key.split('.');
    let itemKey = '';
    do {
      itemKey += keys.shift();
      if (target != null && target[itemKey] != null && (typeof target[itemKey] === 'object' || !keys.length)) {
        target = target[itemKey];
        itemKey = '';
      } else if (target != null) {
        const link = target._links != null ? target._links[itemKey] : null;
        if (link != null) {
          return { link, keys: keys.join('.') };
        }
        const embedded = target._embedded != null ? target._embedded[itemKey] : null;
        if (embedded) {
          target = embedded;
          itemKey = '';
        } else {
          target = undefined;
          itemKey = '';
          break;
        }
      } else {
        target = undefined;
        itemKey = '';
        break;
      }
    } while (keys.length);

    return { target, keys: keys.join('.') };
  }

  // /**
  //  * Returns a translation instantly from the internal state of loaded translation.
  //  * All rules regarding the current language, the preferred language of even fallback languages will be used except any promise handling.
  //  */
  // public instant(key: string, interpolateParams?: Object): string | any {
  //   if (key == null || !key.length) {
  //     throw new Error(`Parameter "key" required`);
  //   }

  //   const [ segment, subKey ] = extractSegments(key);
  //   const content = this.store.getValue(segment, this.config.currentLang);
  //   let res = this.getParsedResult(content, subKey, interpolateParams);

  //   if (typeof res.subscribe !== "undefined") {
  //     return key;
  //   } else {
  //     return res;
  //   }
  // }
}
